import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@template-name/trpc-types";
import { trpc } from "@/trpc/trpc";
import {
  SUBSCRIPTION_STATUS,
  INTERVAL_SHORT,
  clientLabel,
  formatDate,
  formatDateTime,
  zl,
} from "@/components/subscription-labels";

const CHARGE_STATUS: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  scheduled: { label: "Zaplanowane", variant: "outline" },
  processing: { label: "W trakcie", variant: "outline" },
  paid: { label: "Opłacone", variant: "default" },
  failed: { label: "Nieudane", variant: "destructive" },
  refunded: { label: "Zwrócone", variant: "destructive" },
  skipped: { label: "Pominięte", variant: "secondary" },
};

const LEDGER_TYPE: Record<string, string> = {
  granted: "Przyznano",
  redeemed: "Wykorzystano",
  expired: "Wygasło",
  refunded: "Zwrócono",
  adjusted: "Korekta ręczna",
};

const EVENT_TYPE: Record<string, string> = {
  created: "Założono subskrypcję",
  terms_accepted: "Zaakceptowano regulamin",
  activated: "Aktywowano",
  trial_started: "Rozpoczęto okres próbny",
  plan_changed: "Zmiana planu",
  price_changed: "Zmiana ceny",
  mandate_changed: "Zmiana metody płatności",
  charge_paid: "Obciążenie opłacone",
  charge_failed: "Obciążenie nieudane",
  charge_refunded: "Zwrot obciążenia",
  paused: "Wstrzymano",
  resumed: "Wznowiono",
  cancel_requested: "Zgłoszono rezygnację",
  cancelled: "Zakończono (rezygnacja)",
  expired: "Wygasło (brak płatności)",
  reactivated: "Reaktywowano",
  credits_granted: "Przyznano świadczenia",
  credits_expired: "Świadczenia wygasły",
  staff_adjustment: "Korekta personelu",
  notification_sent: "Wysłano powiadomienie",
};

const ACTOR: Record<string, string> = {
  customer: "klientka",
  staff: "personel",
  system: "system",
};

const MANDATE_STATUS: Record<string, string> = {
  pending: "oczekuje",
  active: "aktywna",
  expired: "wygasła",
  revoked: "cofnięta",
  failed: "nieudana",
};

const LIVE_STATUSES = ["incomplete", "trialing", "active", "past_due", "paused"];

type Detail = inferRouterOutputs<AppRouter>["subscription"]["getSubscriber"];

/** Refreshes everything that shows this subscription once an action lands. */
function useRefreshSubscription(subscriptionId: string) {
  const utils = trpc.useUtils();
  return () => {
    utils.subscription.getSubscriber.invalidate({ subscriptionId });
    utils.subscription.listSubscribers.invalidate();
    utils.subscription.listPlans.invalidate();
    utils.dashboard.getStats.invalidate();
    utils.clients.list.invalidate();
  };
}

/**
 * The staff actions. The two that end a membership spell out what will
 * happen before the button does it; the client's own cancel screen makes the
 * same promise, and the desk must not make a weaker one on her behalf.
 * Nothing typed here reaches the journal: it outlives the account.
 */
function StaffActions({ data, isOwner }: { data: Detail; isOwner: boolean }) {
  const [open, setOpen] = useState<"cancel" | "end" | "credits" | null>(null);
  const live = !data.endedAt && LIVE_STATUSES.includes(data.status);
  if (!live) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-3">
      <span className="mr-auto text-xs text-muted-foreground">Akcje personelu</span>
      <Button size="sm" variant="outline" onClick={() => setOpen("credits")}>
        Koryguj świadczenia
      </Button>
      {!data.cancelAtPeriodEnd && (
        <Button size="sm" variant="outline" onClick={() => setOpen("cancel")}>
          Zgłoś rezygnację
        </Button>
      )}
      {isOwner && (
        <Button size="sm" variant="destructive" onClick={() => setOpen("end")}>
          Zakończ natychmiast
        </Button>
      )}

      {open === "cancel" && (
        <CancelDialog data={data} onClose={() => setOpen(null)} />
      )}
      {open === "end" && <EndNowDialog data={data} onClose={() => setOpen(null)} />}
      {open === "credits" && (
        <AdjustCreditsDialog data={data} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

function CancelDialog({ data, onClose }: { data: Detail; onClose: () => void }) {
  const refresh = useRefreshSubscription(data.id);
  const cancel = trpc.subscription.cancelSubscription.useMutation({
    onSuccess: (r) => {
      refresh();
      toast.success(
        `Rezygnacja zgłoszona. Abonament działa do ${formatDate(r.endsAt)}` +
          (r.remainingCharges > 0
            ? `, pozostało ${r.remainingCharges} obciążeń ze zobowiązania.`
            : "."),
      );
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Zgłosić rezygnację?</DialogTitle>
          <DialogDescription>
            To samo, co klientka może zrobić w aplikacji. Abonament działa do końca
            opłaconego okresu
            {data.minCommitmentCycles > 0 && " lub do końca zobowiązania"}, potem nie
            odnawia się. Świadczenia zostają do wykorzystania do tego czasu.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            disabled={cancel.isPending}
            onClick={() => cancel.mutate({ subscriptionId: data.id })}
          >
            Zgłoś rezygnację
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const END_REASONS = [
  { value: "illness", label: "Choroba" },
  { value: "relocation", label: "Przeprowadzka" },
  { value: "settlement", label: "Ugoda z klientką" },
  { value: "dispute", label: "Reklamacja / spór" },
  { value: "other", label: "Inny" },
] as const;
type EndReason = (typeof END_REASONS)[number]["value"];

function EndNowDialog({ data, onClose }: { data: Detail; onClose: () => void }) {
  const [reason, setReason] = useState<EndReason | "">("");
  const refresh = useRefreshSubscription(data.id);
  const end = trpc.subscription.endSubscriptionNow.useMutation({
    onSuccess: (r) => {
      refresh();
      toast.success(
        `Abonament zakończony.` +
          (r.creditsLeftUnredeemed > 0
            ? ` ${r.creditsLeftUnredeemed} świadczeń przepadło.`
            : ""),
      );
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Zakończyć natychmiast?</DialogTitle>
          <DialogDescription>
            Abonament kończy się teraz, bez dalszych obciążeń
            {data.minCommitmentCycles > 0 && " — także tych ze zobowiązania"}. Klientka
            traci dostęp do {data.creditsAvailable} niewykorzystanych świadczeń, a
            opłacony okres nie jest zwracany automatycznie. Zwrot pieniędzy robisz
            osobno w panelu Tpay.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>Powód (trafia do historii jako kategoria, bez szczegółów)</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as EndReason)}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz powód" />
              </SelectTrigger>
              <SelectContent>
                {END_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="destructive"
            disabled={end.isPending || reason === ""}
            onClick={() =>
              reason && end.mutate({ subscriptionId: data.id, reason })
            }
          >
            Zakończ teraz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustCreditsDialog({ data, onClose }: { data: Detail; onClose: () => void }) {
  const targets = data.entitlements;
  const [target, setTarget] = useState(
    targets[0] ? (targets[0].treatmentId ?? targets[0].tagId ?? "") : "",
  );
  const [quantity, setQuantity] = useState("1");
  const [expiresAt, setExpiresAt] = useState("");
  const [reason, setReason] = useState("");
  const refresh = useRefreshSubscription(data.id);
  const adjust = trpc.subscription.adjustCredits.useMutation({
    onSuccess: (r) => {
      refresh();
      toast.success(`Zapisano. Do wykorzystania: ${r.creditsAvailable}`);
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const chosen = targets.find((t) => (t.treatmentId ?? t.tagId) === target);
  const qty = quantity.trim() === "" ? NaN : Number(quantity);
  const valid = chosen && Number.isInteger(qty) && qty !== 0 && reason.trim().length >= 3;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Korekta świadczeń</DialogTitle>
          <DialogDescription>
            Dodatnia liczba to gest dla klientki — świadczenie działa jak opłacone.
            Ujemna zabiera niewykorzystane. Każda korekta zostaje w księdze z Twoim
            nazwiskiem i powodem.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Świadczenie</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((t) => {
                    const v = t.treatmentId ?? t.tagId ?? "";
                    return (
                      <SelectItem key={v} value={v}>
                        {t.name}
                        {t.maxRedemptionValue !== null && ` (do ${zl(t.maxRedemptionValue)})`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="adj-qty">Ile (ujemne = zabierz)</Label>
                <Input
                  id="adj-qty"
                  type="number"
                  min={-20}
                  max={20}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adj-exp">Ważne do (opcjonalnie)</Label>
                <Input
                  id="adj-exp"
                  type="date"
                  value={expiresAt}
                  disabled={qty < 0}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Bez daty świadczenie wygasa jak te z bieżącego okresu, czyli po{" "}
              {data.creditsExpireAfterCycles} kolejnych okresach.
            </p>
            <div className="space-y-2">
              <Label htmlFor="adj-reason">Powód (wymagany, bez danych osobowych)</Label>
              <Input
                id="adj-reason"
                value={reason}
                maxLength={200}
                onChange={(e) => setReason(e.target.value)}
                placeholder="np. przeprosiny za odwołaną wizytę"
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            disabled={!valid || adjust.isPending}
            onClick={() =>
              adjust.mutate({
                subscriptionId: data.id,
                quantity: qty,
                treatmentId: chosen?.treatmentId ?? null,
                tagId: chosen?.tagId ?? null,
                expiresAt:
                  qty > 0 && expiresAt
                    ? new Date(`${expiresAt}T23:59:59`).toISOString()
                    : null,
                reason: reason.trim(),
              })
            }
          >
            Zapisz korektę
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}

/**
 * One subscription in full — the support view. Everything here is read from
 * the journals (charges, credit ledger, events), not from the current row,
 * so it answers "why was she charged" and "what does she still have" from
 * evidence rather than from state.
 */
export function SubscriptionDetailDialog({
  subscriptionId,
  onClose,
}: {
  subscriptionId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = trpc.subscription.getSubscriber.useQuery({
    subscriptionId,
  });
  const { data: me } = trpc.meRouter.getMe.useQuery();
  const isOwner = me?.salonRole === "owner";

  const status = data ? SUBSCRIPTION_STATUS[data.status] : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {data ? clientLabel(data.client) : "Subskrypcja"}
            {status && <Badge variant={status.variant}>{status.label}</Badge>}
            {data?.cancelAtPeriodEnd && !data.endedAt && (
              <Badge variant="outline">rezygnacja z końcem okresu</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {data && (
              <>
                {data.plan.name} · {zl(data.price)} {INTERVAL_SHORT[data.billingInterval]}
                {data.discountPercent > 0 && ` · −${data.discountPercent}% na resztę oferty`}
                {" · "}sprzedano w wersji planu {data.planVersion}
                {data.plan.version !== data.planVersion &&
                  ` (obecnie ${data.plan.version}, cena ${zl(data.plan.price)})`}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Ładowanie...</p>
          )}

          {data && (
            <>
              <dl className="grid grid-cols-2 gap-3 rounded-md border p-4 sm:grid-cols-4">
                <Field label="E-mail" value={data.client.email} />
                <Field label="Od" value={formatDate(data.startedAt)} />
                <Field label="Opłaconych okresów" value={data.cyclesBilled} />
                <Field
                  label="Bieżący okres do"
                  value={data.currentPeriodEnd ? formatDate(data.currentPeriodEnd) : null}
                />
                <Field
                  label="Następne obciążenie"
                  value={data.nextChargeAt ? formatDate(data.nextChargeAt) : "brak"}
                />
                <Field
                  label="Świadczenia do wykorzystania"
                  value={
                    <>
                      {data.creditsAvailable}
                      {data.nextCreditExpiresAt && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          (najbliższe wygasa {formatDate(data.nextCreditExpiresAt)})
                        </span>
                      )}
                    </>
                  }
                />
                <Field label="Wykorzystane łącznie" value={data.creditsRedeemed} />
                <Field
                  label="Metoda płatności"
                  value={
                    data.mandate
                      ? `${data.mandate.label ?? (data.mandate.method === "blik_alias" ? "BLIK" : "Karta")} · ${
                          MANDATE_STATUS[data.mandate.status] ?? data.mandate.status
                        }${
                          data.mandate.expiresAt
                            ? `, ważna do ${formatDate(data.mandate.expiresAt)}`
                            : ""
                        }`
                      : "brak"
                  }
                />
                {data.dunningAttempt > 0 && (
                  <Field
                    label="Windykacja"
                    value={`próba ${data.dunningAttempt}${
                      data.dunningStartedAt ? `, od ${formatDate(data.dunningStartedAt)}` : ""
                    }`}
                  />
                )}
                {data.cancelledAt && (
                  <Field label="Rezygnacja zgłoszona" value={formatDate(data.cancelledAt)} />
                )}
                {data.endedAt && <Field label="Zakończona" value={formatDate(data.endedAt)} />}
                {data.minCommitmentCycles > 0 && (
                  <Field label="Zobowiązanie" value={`${data.minCommitmentCycles} okresów`} />
                )}
                <Field
                  label="Świadczenia przenoszą się"
                  value={`${data.creditsExpireAfterCycles} okresów`}
                />
                {data.acceptedTermsVersion && (
                  <Field label="Regulamin" value={`wersja ${data.acceptedTermsVersion}`} />
                )}
              </dl>

              <StaffActions data={data} isOwner={isOwner} />

              <Tabs defaultValue="charges">
                <TabsList className="w-full">
                  <TabsTrigger value="charges" className="flex-1">
                    Obciążenia ({data.charges.length})
                  </TabsTrigger>
                  <TabsTrigger value="ledger" className="flex-1">
                    Świadczenia ({data.ledger.length})
                  </TabsTrigger>
                  <TabsTrigger value="events" className="flex-1">
                    Historia ({data.events.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="charges">
                  {data.charges.length === 0 ? (
                    <p className="py-4 text-sm text-muted-foreground">
                      Jeszcze żadnego obciążenia.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-2 pr-3 font-medium">Okres</th>
                          <th className="py-2 pr-3 font-medium">Kwota</th>
                          <th className="py-2 pr-3 font-medium">Status</th>
                          <th className="py-2 pr-3 font-medium">Zapłacono</th>
                          <th className="py-2 font-medium">Szczegóły</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.charges.map((c) => {
                          const cs = CHARGE_STATUS[c.status] ?? {
                            label: c.status,
                            variant: "outline" as const,
                          };
                          return (
                            <tr key={c.id} className="border-b last:border-0">
                              <td className="py-2 pr-3">
                                <span className="text-muted-foreground">#{c.periodIndex + 1}</span>{" "}
                                {formatDate(c.periodStart)} – {formatDate(c.periodEnd)}
                              </td>
                              <td className="py-2 pr-3 font-medium">{zl(c.amount)}</td>
                              <td className="py-2 pr-3">
                                <Badge variant={cs.variant}>{cs.label}</Badge>
                                {c.attempt > 0 && (
                                  <span className="ml-1 text-xs text-muted-foreground">
                                    próba {c.attempt}
                                  </span>
                                )}
                              </td>
                              <td className="py-2 pr-3 text-muted-foreground">
                                {c.paidAt ? formatDateTime(c.paidAt) : "—"}
                              </td>
                              <td className="py-2 text-xs text-muted-foreground">
                                {c.failureCode && (
                                  <div>
                                    {c.failureCode}
                                    {c.failureMessage && `: ${c.failureMessage}`}
                                  </div>
                                )}
                                {c.tpayTransactionId && (
                                  <div className="font-mono">Tpay: {c.tpayTransactionId}</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </TabsContent>

                <TabsContent value="ledger">
                  {data.ledger.length === 0 ? (
                    <p className="py-4 text-sm text-muted-foreground">
                      Księga świadczeń jest pusta — zapełni się po pierwszej opłacie.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-2 pr-3 font-medium">Data</th>
                          <th className="py-2 pr-3 font-medium">Zdarzenie</th>
                          <th className="py-2 pr-3 font-medium">Ile</th>
                          <th className="py-2 pr-3 font-medium">Na co</th>
                          <th className="py-2 font-medium">Szczegóły</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.ledger.map((e) => (
                          <tr key={e.id} className="border-b last:border-0">
                            <td className="py-2 pr-3 text-muted-foreground">
                              {formatDate(e.createdAt)}
                            </td>
                            <td className="py-2 pr-3">{LEDGER_TYPE[e.type] ?? e.type}</td>
                            <td
                              className={`py-2 pr-3 font-medium ${
                                e.quantity > 0 ? "text-green-700" : "text-muted-foreground"
                              }`}
                            >
                              {e.quantity > 0 ? `+${e.quantity}` : e.quantity}
                            </td>
                            <td className="py-2 pr-3">
                              {e.treatmentName ?? (e.tagName ? `dowolny: ${e.tagName}` : "—")}
                              {e.maxRedemptionValue !== null && (
                                <span className="ml-1 text-xs text-muted-foreground">
                                  do {zl(e.maxRedemptionValue)}
                                </span>
                              )}
                            </td>
                            <td className="py-2 text-xs text-muted-foreground">
                              {e.expiresAt && e.quantity > 0 && (
                                <div>ważne do {formatDate(e.expiresAt)}</div>
                              )}
                              {e.purchaseCode && (
                                <div className="font-mono">zamówienie {e.purchaseCode}</div>
                              )}
                              {e.createdBy && <div>przez: {e.createdBy}</div>}
                              {e.description && <div>{e.description}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </TabsContent>

                <TabsContent value="events">
                  {data.events.length === 0 ? (
                    <p className="py-4 text-sm text-muted-foreground">Brak zapisów.</p>
                  ) : (
                    <ol className="space-y-3 py-2">
                      {[...data.events].reverse().map((e) => (
                        <li key={e.id} className="border-l-2 pl-4">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="font-medium">
                              {EVENT_TYPE[e.type] ?? e.type}
                              {e.channel && (
                                <span className="ml-1 text-xs font-normal text-muted-foreground">
                                  ({e.channel})
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(e.occurredAt)} ·{" "}
                              {e.actorName ?? ACTOR[e.actor] ?? e.actor}
                            </span>
                          </div>
                          <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                            {e.termsVersion && <div>Regulamin: {e.termsVersion}</div>}
                            {e.messageId && (
                              <div className="font-mono break-all">Wiadomość: {e.messageId}</div>
                            )}
                            {e.payload && Object.keys(e.payload).length > 0 && (
                              <div className="font-mono break-all">
                                {JSON.stringify(e.payload)}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
